import React, { useEffect } from 'react';
import { compose } from 'ramda';
import queryString from 'query-string';
import styled from 'styled-components/macro';
import { showToast } from '@deeptrue-frontend/ui';

import Link from 'components/RouterProvider/Link';
import Cookies from 'js-cookie';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import { SidePage } from 'components/Layout';
import api from 'api/api';
import QUERIES from 'consts/queries';
import env from 'consts/env';
import AUTH from 'consts/auth';
import signInSplash from 'assets/images/sign-in.png';
import { parseErrors } from 'components/Forms/utils';
import SignInForm, { SubmitHandler } from './SignInForm';
import { useFormatMessage } from 'components/IntlProvider';
import { Typography, TextLink } from '@deeptrue-frontend/ui';

const Footer = styled.footer`
  text-align: center;
  margin-top: auto;
`;

const { connect, actions } = StoreProvider;

const SignIn = ({ signIn, history: { location }, setLanguage }: any) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState();
  const t = useFormatMessage();
  const defaultLanguage = 'en';
  const { action, redirect_url, lang = defaultLanguage } = queryString.parse(
    location.search
  );
  const isProduction = env.IS_NODE_ENV_PRODUCTION;
  const { state } = location;

  useEffect(() => {
    if (action === QUERIES.PASS_RESET) {
      showToast({
        type: 'success',
        title: t(
          'action.passwordreset.success',
          'Your password was successfully changed.'
        ),
      });
    }
    // eslint-disable-next-line
  }, [action]);

  const onSubmit: SubmitHandler = async (values) => {
    try {
      setIsSubmitting(true);
      const { data } = await api.resources.signInUser(values);

      setLanguage(data.user.language);
      Cookies.set(AUTH.TOKEN, `${data.token}`, {
        path: '/',
        domain: isProduction ? '.deeptrue.com' : 'localhost',
      });

      if (redirect_url && !Array.isArray(redirect_url)) {
        window.location.replace(redirect_url);
      } else {
        signIn(data);
      }
    } catch (err) {
      if (err.response.status === 401 && err.response.data.show === 'yes') {
        showToast({
          type: 'success',
          title: err.response.data.detail,
        });
      }

      setSubmitError(parseErrors(err.response.data)._error || '');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isRedirectFromEmailTaken = action === 'email_taken';

  return (
    <SidePage
      title={t('forms.signin.signin', 'Sign In to deeptrue')}
      splashImage={signInSplash}
      slogan={
        <Typography color="#fff" mt="6rem" variant="h2">
          {t(
            'pages.auth.header',
            "On deeptrue's workbenches you will find well-founded, ready-to-go insight tools"
          )}
        </Typography>
      }
    >
      <Typography alignSelf="start" mt="1.5rem" mb="2rem">
        {t('forms.signin.noaccount', 'Don’t have an account?')}
        &nbsp;
        <Link to="/sign-up">
          <TextLink>{t('forms.signin.signuplink', 'Sign Up')}</TextLink>
        </Link>
      </Typography>
      <SignInForm
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        submitError={submitError}
        initialValues={{
          email: isRedirectFromEmailTaken ? state : '',
          password: '',
        }}
      />
      <Footer>
        <Typography variant="copy3" mt="2rem" mb="1rem" textAlign="center">
          {t(
            'forms.signin.agreements',
            `By clicking ’Sign In’, you agree to deeptrue's`
          )}
          <br />
          <a
            href={`https://deeptrue.com/${
              lang || defaultLanguage
            }/terms-and-conditions`}
          >
            <TextLink>
              {t('forms.signin.agreements2', 'Terms of Service')}
            </TextLink>
          </a>{' '}
          <span>{t('forms.signin.agreements3', 'and')}</span>{' '}
          <a
            href={`https://deeptrue.com/${
              lang || defaultLanguage
            }/privacy-policy`}
          >
            <TextLink>
              {t('forms.signin.agreements4', 'Privacy Policy')}
            </TextLink>
          </a>
          {t('forms.signin.agreements5', '.')}
        </Typography>
      </Footer>
    </SidePage>
  );
};

export default compose(
  connect(null, {
    signIn: actions.authActions.signIn,
    setLanguage: actions.intlActions.setLanguage,
  }),
  withMetaDescription({ title: 'Dashboard login' })
)(SignIn);
